import { useCallback, useEffect } from 'react';

import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useSendMessageMutation } from 'api/graphql';
import { File } from 'components/ui/forms';
import { Button, Tooltip } from 'components/ui/general';
import { useToast } from 'hooks';

import { texts } from './SendFiles.text';

import styles from './SendFiles.module.scss';

type SendFilesProps = {
  channelId?: string | number;
  disabled?: boolean;
};

export const SendFiles = ({ channelId, disabled }: SendFilesProps) => {
  const { addToast } = useToast();
  const { formatMessage } = useIntl();
  const [sendMessage, { loading, error }] = useSendMessageMutation();
  const { register } = useForm<any>();

  useEffect(() => {
    if (error) {
      addToast({
        title: formatMessage(texts.sendFilesError),
        message: error.message,
        type: 'error',
      });
    }
  }, [addToast, error, formatMessage]);

  const onChange = useCallback(
    async ({ target }: any) => {
      if (target.files?.length && channelId) {
        await sendMessage({
          variables: {
            input: {
              channelId,
              message: ' ', // Will not be rendered
              files: target.files,
            },
          },
        });

        // Reset so onChange works with the same file(s)
        target.value = '';
      }
    },
    [channelId, sendMessage]
  );

  return (
    <Tooltip tip={<FormattedMessage {...texts.tooltip} />} trigger="hover">
      <File
        name="files"
        register={register}
        multiple
        disabled={disabled || loading}
        onChange={onChange}
        className={styles.file}
        classNameInner={styles.fileInner}
        classNameLabel={styles.fileLabel}
      >
        <Button
          fullWidth
          size="lg"
          className={styles.fileButton}
          color="secondary"
          iconRight={{ name: 'paperclip' }}
          loading={loading}
          as="div"
        />
      </File>
    </Tooltip>
  );
};
